import React, { useState } from "react";
import { BookOpen, RotateCcw, Loader2, AlertCircle } from "lucide-react";
import { journalEntriesApi } from "../../services/api";
import PremiumModal from "../ui/PremiumModal";

interface JournalLine {
  accountId: { _id: string; code: string; name: string; type: string } | string;
  debitCents: number;
  creditCents: number;
  matterId?: { _id: string; title: string } | string | null;
  clientId?: { _id: string; displayName: string } | string | null;
  memo?: string | null;
}

interface JournalEntry {
  _id: string;
  postedAt: string;
  postedBy?: { name?: string; email?: string } | null;
  currency: string;
  memo: string | null;
  source: { type: string; id: string };
  lines: JournalLine[];
  reversedByEntryId?: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  entry: JournalEntry | null;
  onReversed: () => void;
}

function formatCents(cents: number): string {
  if (cents === 0) return "-";
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
  }).format(cents / 100);
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function sourceLabel(type: string): string {
  const labels: Record<string, string> = {
    trust_receipt: "Trust Receipt",
    trust_disbursement: "Trust Disbursement",
    invoice: "Invoice",
    payment: "Payment",
    transfer: "Transfer",
    correction: "Correction",
    manual: "Manual",
  };
  return labels[type] || type;
}

const JournalEntryDetailModal: React.FC<Props> = ({ open, onClose, entry, onReversed }) => {
  const [reversing, setReversing] = useState(false);
  const [reverseError, setReverseError] = useState("");

  if (!entry) return null;

  const totalDebits = entry.lines.reduce((sum, l) => sum + (l.debitCents || 0), 0);
  const totalCredits = entry.lines.reduce((sum, l) => sum + (l.creditCents || 0), 0);
  const postedByName =
    typeof entry.postedBy === "object" && entry.postedBy
      ? entry.postedBy.name || entry.postedBy.email || "System"
      : "System";

  const handleReverse = async () => {
    if (!window.confirm("Are you sure you want to reverse this journal entry? This will create an offsetting entry.")) return;
    setReversing(true);
    setReverseError("");
    try {
      await journalEntriesApi.reverse(entry._id);
      onReversed();
      onClose();
    } catch (err: any) {
      setReverseError(err.response?.data?.error || "Failed to reverse entry");
    } finally {
      setReversing(false);
    }
  };

  return (
    <PremiumModal
      open={open}
      onClose={onClose}
      title="Journal Entry Detail"
      icon={BookOpen}
      size="lg"
      footer={
        <div className="flex justify-between items-center w-full">
          <div>
            {!entry.reversedByEntryId && (
              <button
                onClick={handleReverse}
                disabled={reversing}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
              >
                {reversing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4" />
                )}
                Reverse Entry
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      }
    >
      {reverseError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {reverseError}
        </div>
      )}

      {entry.reversedByEntryId && (
        <div className="mb-4 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          This entry has been reversed.
        </div>
      )}

      {/* Metadata */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <p className="text-xs text-slate-500 font-medium">Posted</p>
          <p className="text-sm text-slate-800 font-medium">{formatDate(entry.postedAt)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium">Posted By</p>
          <p className="text-sm text-slate-800 font-medium">{postedByName}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium">Source</p>
          <p className="text-sm text-slate-800 font-medium">{sourceLabel(entry.source.type)}</p>
        </div>
        <div>
          <p className="text-xs text-slate-500 font-medium">Currency</p>
          <p className="text-sm text-slate-800 font-medium">{entry.currency}</p>
        </div>
        {entry.memo && (
          <div className="col-span-2">
            <p className="text-xs text-slate-500 font-medium">Memo</p>
            <p className="text-sm text-slate-800">{entry.memo}</p>
          </div>
        )}
      </div>

      {/* Lines Table */}
      <div className="overflow-x-auto rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white text-left" style={{ backgroundColor: "var(--color-primary)" }}>
              <th className="px-4 py-3 font-semibold">Code</th>
              <th className="px-4 py-3 font-semibold">Account</th>
              <th className="px-4 py-3 font-semibold text-right">Debit</th>
              <th className="px-4 py-3 font-semibold text-right">Credit</th>
              <th className="px-4 py-3 font-semibold">Memo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {entry.lines.map((line, i) => {
              const account =
                typeof line.accountId === "object" && line.accountId
                  ? line.accountId
                  : null;
              return (
                <tr key={i} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-600">
                    {account?.code || "-"}
                  </td>
                  <td className="px-4 py-3 text-slate-800 font-medium">
                    {account?.name || "Unknown Account"}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">
                    {formatCents(line.debitCents || 0)}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-800">
                    {formatCents(line.creditCents || 0)}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {line.memo || "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-slate-50 font-bold">
              <td className="px-4 py-3" colSpan={2}>
                Totals
              </td>
              <td className="px-4 py-3 text-right font-mono text-slate-800">
                {formatCents(totalDebits)}
              </td>
              <td className="px-4 py-3 text-right font-mono text-slate-800">
                {formatCents(totalCredits)}
              </td>
              <td className="px-4 py-3"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </PremiumModal>
  );
};

export default JournalEntryDetailModal;
